import { Section } from "../components/Section";
import { useLbStats, useLbReviews, type LbReview } from "../lib/api";
import { stars, fmtDate, fmtReview } from "../lib/format";

const TAPES = 12; // two rows of six on desktop

function LbStatTile({
  value,
  label,
  meter,
}: {
  value: string;
  label: string;
  meter?: number; // 0..1 → renders a segmented tach gauge under the readout
}) {
  return (
    <div className="screen flex flex-col items-start gap-2 p-4">
      <span className="text-glow font-dseg text-[32px] leading-none tabular-nums text-amber">
        {value}
      </span>
      {meter != null && (
        <div className="segbar w-full">
          <div className="segbar__fill" style={{ width: `${Math.round(meter * 100)}%` }} />
        </div>
      )}
      <span className="vfd-box">{label}</span>
    </div>
  );
}

export function Letterboxd() {
  const { data: s } = useLbStats();
  const { reviews, loading, error } = useLbReviews(TAPES);

  return (
    <Section id="letterboxd" label="letterboxd" title="letterboxd">
      <p className="mt-5 text-text-dim">
        <strong className="text-text">what am I watching lately?</strong> Letterboxd (annoyingly)
        doesn't allow public API access, so this is{" "}
        <a href="https://movies.grstnhbr.com/apis/" target="_blank" rel="noopener">
          my own data
        </a>
        , curated and enriched by{" "}
        <a href="https://www.themoviedb.org/" target="_blank" rel="noopener">
          TMDB
        </a>{" "}
        (who does allow API access). Feel free to poke through my reviews here, but it probably
        makes more sense to go{" "}
        <a href="https://letterboxd.com/_aj/" target="_blank" rel="noopener">
          check out Letterboxd
        </a>
        .
      </p>

      {/* 5 hero stat tiles — 7-segment VFD readouts */}
      <div className="mt-7 grid grid-cols-[repeat(auto-fit,minmax(148px,1fr))] gap-3.5">
        <LbStatTile value={s ? String(s.films_watched) : "—"} label="films watched" />
        <LbStatTile value={s ? String(s.reviews) : "—"} label="reviews written" />
        <LbStatTile
          value={s && s.avg_rating != null ? s.avg_rating.toFixed(2) : "—"}
          meter={s && s.avg_rating != null ? s.avg_rating / 5 : undefined}
          label="avg rating ★"
        />
        <LbStatTile value={s ? String(s.films_this_year) : "—"} label="watched this year" />
        <LbStatTile value={s ? String(s.lists) : "—"} label="lists" />
      </div>

      {/* Recent reviews: posters as VHS tapes on the shared grid-paper board
          (same pattern as "cars i've owned"). Hover / focus / tap shows the
          whole review beside the tape, in a card that scrolls. */}
      <h3 className="etch-label mt-10 !text-[12px] !tracking-[0.16em] !text-amber">
        recent reviews
      </h3>
      <div className="board panel mt-4">
        {loading && <div className="py-2 text-[13px] text-text-mute">loading recent reviews…</div>}
        {error && (
          <div className="py-2 text-[13px] text-text-mute">Couldn't load reviews right now.</div>
        )}
        {!loading && !error && reviews && reviews.length === 0 && (
          <div className="py-2 text-[13px] text-text-mute">No reviews yet.</div>
        )}
        {reviews && reviews.length > 0 && (
          <ol className="board-grid board-grid--tapes">
            {reviews.map((r, i) => (
              <Tape key={`${r.film.slug}-${i}`} review={r} popLeft={i % 6 >= 3} />
            ))}
          </ol>
        )}
      </div>
    </Section>
  );
}

function Tape({ review, popLeft }: { review: LbReview; popLeft: boolean }) {
  const t = review.tmdb ?? {};
  // w342 is plenty for a tape-sized poster (the API hands back w500).
  const poster = t.poster_url?.replace("/w500/", "/w342/");
  const film = review.film;
  const title = film.year ? `${film.name} (${film.year})` : film.name;
  const meta: string[] = [];
  if (t.enriched && t.runtime) meta.push(`${t.runtime} min`);
  if (t.genres && t.genres.length) meta.push(t.genres.slice(0, 2).join(" · "));

  // Not a link: Letterboxd has no stable per-review URL for these. The whole
  // review is in the card instead, which scrolls.
  return (
    <li
      className={`board-cell${popLeft ? " pop-left" : ""}`}
      tabIndex={0}
      aria-label={`${title}, ${stars(review.rating)}`}
    >
      <div className="tape">
        <div className="tape-front">
          {poster ? (
            <img src={poster} alt="" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center p-2 text-center text-[11px] text-[#e9e4d6]">
              {film.name}
            </div>
          )}
        </div>
      </div>

      <div className="board-pop board-pop--scroll" role="tooltip">
        <div className="board-pop-inner panel">
        <div className="etch-label !text-[11px] !text-amber">
          watched {fmtDate(review.watched_date)}
          {review.rewatch ? " · rewatch" : ""}
        </div>
        <div className="mt-1 font-display text-[15px] font-semibold uppercase leading-snug tracking-wide">
          {title}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[13px]">
          <span className="font-mono text-[15px] tracking-wide text-green">{stars(review.rating)}</span>
          {meta.length > 0 && <span className="text-text-dim">{meta.join(" · ")}</span>}
        </div>
        <div
          className="tape-review mt-2.5 text-[13.5px] leading-relaxed text-text [&_em]:text-text [&_i]:text-text"
          dangerouslySetInnerHTML={{ __html: fmtReview(review.review_html) }}
        />
        </div>
      </div>
    </li>
  );
}
